"use client";

import { UsrcReferralSteps } from "@/configuration/groups/usrc/components/referral-steps";
import { ReferralWorkflowLayout } from "@/configuration/groups/usrc/components/referral-timeline";
import {
  intakeManagerLabel,
  USRC_ADMIT_TYPES,
  USRC_MODALITY_TYPES,
} from "@/configuration/groups/usrc/intake-manager-ids";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import { ImagingResultsTable } from "@/src/components/cards/imaging-results-card/imaging-results-table";
import { LabResultsTable } from "@/src/components/cards/lab-results-card/lab-results-table";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import { ProgressNoteTable } from "@/src/components/cards/progress-note-card/progress-note-table";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import ErrorAlert from "@/src/components/error-alert";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useEnabledFields } from "@/src/lib/data-fetchers/schema-fetchers";
import {
  Classification,
  isImagingResultsClassification,
  isLabResultsClassification,
  isProgressNoteClassification,
} from "@/src/models/constants/classifications";
import {
  ImagingResultsDocumentType,
  LabResultsDocumentType,
  ProgressNoteDocumentType,
} from "@/src/models/constants/document-types";
import { Specialty } from "@/src/models/constants/specialty";
import { ImagingResults } from "@/src/models/documents/imaging/imaging-results";
import { LabResults } from "@/src/models/documents/lab-results/lab-results";
import { MedicalHistory } from "@/src/models/documents/medical-history/medical-history";
import { ProgressNote } from "@/src/models/documents/progress-note/progress-note";
import { Referral } from "@/src/models/documents/referral";
import { FileType, File } from "@/src/models/file";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";
import type { TaskStep } from "@/src/models/task-step";

function USRCReferralInstructionsContent({ referral, sections }: { referral: Referral; sections: Section[] }) {
  const referringLocation = referral.referringLocation;
  const receivingLocation = referral.receivingLocation;

  // Helper function to get location name
  const getLocationName = (location: { name?: string | null } | null | undefined) => {
    if (!location) return "N/A";

    return location.name || "N/A";
  };

  const isMedicalHistoryChartClassification = (classification: Classification) => {
    return (
      classification === Classification.MEDICAL_HISTORY_CHART_FROM_CLINIC ||
      classification === Classification.MEDICAL_HISTORY_CHART_FROM_HOSPITAL
    );
  };

  // Helper function to get nephrology progress notes from task sections
  const getNephrologyProgressNotes = () => {
    return (
      sections
        ?.filter((section) => isProgressNoteClassification(section.classification))
        .map((section) => ({
          ...(section.reviewedContent as ProgressNote),
          classification: section.classification,
        }))
        .filter((progressNote) => (progressNote as ProgressNote).provider?.specialty?.includes(Specialty.NEPHROLOGY)) ||
      []
    );
  };

  // Helper function to get nephrology medical chart from task sections
  const getNephrologyMedicalChart = () => {
    return (
      sections
        ?.filter((section) => isMedicalHistoryChartClassification(section.classification))
        .map((section) => ({
          ...(section.reviewedContent as MedicalHistory),
          classification: section.classification,
        }))
        .filter((medicalHistory) =>
          (medicalHistory as MedicalHistory).provider?.specialty?.includes(Specialty.NEPHROLOGY)
        ) || []
    );
  };

  // Get nephrology progress notes data once
  const nephrologyProgressNotes = getNephrologyProgressNotes();
  const firstProgressNote = nephrologyProgressNotes.length > 0 ? (nephrologyProgressNotes[0] as ProgressNote) : null;

  // Get nephrology medical chart data once
  const nephrologyMedicalChart = getNephrologyMedicalChart();
  const firstMedicalChart = nephrologyMedicalChart.length > 0 ? (nephrologyMedicalChart[0] as MedicalHistory) : null;

  // Helper function to get diagnosis from progress notes
  const getDiagnosis = () => {
    if (!firstProgressNote && !firstMedicalChart) {
      return "N/A";
    }

    // Get diagnoses from progress note or medical chart. usrc enables
    // `documentInformation.diagnosisList` on both MEDICAL_HISTORY_CHART_* classifications, so the
    // chart is a real source here (see the `diagnosisList` note on `MedicalHistory`).
    const diagnoses = firstProgressNote?.diagnosisList ?? firstMedicalChart?.diagnosisList;

    if (!diagnoses || diagnoses.length === 0) {
      return "N/A";
    }

    // Format diagnoses without codes
    return diagnoses.map((diagnosis) => diagnosis.diagnosisText).join("; ");
  };

  // Helper function to get referring physician from progress note
  const getReferringPhysician = () => {
    if (!firstProgressNote && !firstMedicalChart) {
      return { name: "N/A", npi: "N/A" };
    }

    // Get provider from progress note or medical chart
    const provider = firstProgressNote?.provider || firstMedicalChart?.provider;

    if (!provider) {
      return { name: "N/A", npi: "N/A" };
    }

    const name = `${provider.givenName || ""} ${provider.familyName || ""}`.trim();
    const npi = provider.npiNumber || "";

    return { name: name || "N/A", npi: npi || "N/A" };
  };

  const referringPhysician = getReferringPhysician();

  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium text-center">{"USRC"} Referral Instructions:</span>

      <span className="text-muted-foreground">
        <ul className="space-y-3 text-sm">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Verify that the patient information matches what&apos;s in their system</span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              <strong>Check SSN:</strong> <CopyableContent text="if blank, fill in with 4’s" />
            </span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <div>
              <span>
                <strong>Set Admit Type:</strong>
              </span>
              <ul className="ml-4 mt-1 space-y-1">
                <li>
                  • If patient has NOT received outpatient HD before, patient is{" "}
                  <CopyableContent text={intakeManagerLabel(USRC_ADMIT_TYPES, 85)} />
                </li>
                <li>
                  • If patient has received outpatient HD before, patient is{" "}
                  <CopyableContent text={intakeManagerLabel(USRC_ADMIT_TYPES, 88)} />
                </li>
              </ul>
            </div>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              <strong>Set Intended facility (aka Receiving location):</strong>{" "}
              <CopyableContent text={getLocationName(receivingLocation)} />
            </span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <div>
              <span>
                <strong>Patient diagnosis:</strong> <CopyableContent text={getDiagnosis()} />
              </span>
              <ul className="ml-4 mt-1 space-y-1">
                <li>
                  • CKD 4 and under is <CopyableContent text="AKI" />
                </li>
                <li>
                  • CKD 5 is <CopyableContent text="ESRD" />
                </li>
              </ul>
            </div>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <div>
              <span>
                <strong>Patient modality:</strong>
              </span>
              <ul className="ml-4 mt-1 space-y-1">
                <li>
                  • If diagnosis is AKI, then <CopyableContent text={intakeManagerLabel(USRC_MODALITY_TYPES, 1448)} />
                </li>
                <li>
                  • If diagnosis is chronic, then <CopyableContent text={intakeManagerLabel(USRC_MODALITY_TYPES, 53)} />
                </li>
                <li className="text-xs text-muted-foreground">
                  Examples of chronic dx: ESRD, Stage 4 kidney disease, Chronic kidney disease, etc.
                </li>
              </ul>
            </div>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <div>
              <span>
                <strong>Intended start date:</strong>
              </span>
              <ul className="ml-4 mt-1 space-y-1">
                <li>• Should be 2 business days from now if it aligns with patient day preference</li>
                <li>• If 2 business days lands on non-preferred day, choose the next day</li>
                <li className="text-xs text-muted-foreground">Patient preference examples: MonWedFri, TueThuSat</li>
              </ul>
            </div>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              <strong>Referred by (aka Referring Location):</strong>{" "}
              <CopyableContent text={getLocationName(referringLocation)} />
            </span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              <strong>Facility received referral:</strong> <CopyableContent text={getLocationName(receivingLocation)} />
            </span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <div>
              <span>
                <strong>Referring physician:</strong>
              </span>
              <div className="mt-1">
                <span className="text-sm">
                  <strong>Name:</strong> <CopyableContent text={referringPhysician.name} />
                </span>
                <span className="text-sm ml-4">
                  <strong>NPI:</strong> <CopyableContent text={referringPhysician.npi} />
                </span>
              </div>
            </div>
          </li>
        </ul>
      </span>
    </div>
  );
}

function USRCReferralCards({ referral, sections }: { referral: Referral; sections: Section[] }) {
  const { data: enabledFields } = useEnabledFields();

  const labResultsData =
    sections
      ?.filter((section) => isLabResultsClassification(section.classification))
      .map((section) => ({
        ...(section.reviewedContent as LabResults),
        classification: section.classification,
      })) || [];

  const progressNotesData =
    sections
      ?.filter((section) => isProgressNoteClassification(section.classification))
      .map((section) => ({
        ...(section.reviewedContent as ProgressNote),
        classification: section.classification,
      }))
      // Show all progress notes if classification is flow sheet, otherwise only show for Nephrologists
      .filter(
        (progressNote) =>
          progressNote.classification === Classification.PROGRESS_NOTE_FLOW_SHEET ||
          (progressNote as ProgressNote).provider?.specialty?.includes(Specialty.NEPHROLOGY)
      ) || [];

  const imagingResultsData =
    sections
      ?.filter((section) => isImagingResultsClassification(section.classification))
      .map((section) => ({
        ...(section.reviewedContent as ImagingResults),
        classification: section.classification,
      })) || [];

  return (
    <>
      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage!} type="Primary" isEditable />}
      {referral.secondaryCoverage && (
        <CoverageCard coverage={referral.secondaryCoverage!} type="Secondary" isEditable />
      )}

      {/* Render Progress Notes Table */}
      {progressNotesData.length > 0 && (
        <ProgressNoteTable
          progressNotes={progressNotesData}
          enabledFields={enabledFields ? enabledFields[ProgressNoteDocumentType.PROGRESS_NOTE] : null}
        />
      )}

      {/* Render Lab Results Table */}
      {labResultsData.length > 0 && (
        <LabResultsTable
          labResults={labResultsData}
          enabledFields={enabledFields ? enabledFields[LabResultsDocumentType.LAB_RESULTS] : null}
        />
      )}

      {/* Render Imaging Results Table */}
      {imagingResultsData.length > 0 && (
        <ImagingResultsTable
          imagingResults={imagingResultsData}
          enabledFields={enabledFields ? enabledFields[ImagingResultsDocumentType.IMAGING_RESULTS] : null}
        />
      )}
    </>
  );
}

interface USRCReferralInstructionsProps {
  view: "information" | "steps";
  referral: Referral;
  task: Task;
  taskSteps: TaskStep[];
}

export function USRCReferralInstructions({ view, referral, task, taskSteps }: USRCReferralInstructionsProps) {
  const inboundFile = task.files?.find((file: File) => file.type === FileType.INBOUND);
  const sections = inboundFile?.sections || [];

  if (view === "steps") {
    return (
      <ReferralWorkflowLayout>
        <UsrcReferralSteps task={task} taskSteps={taskSteps} />
      </ReferralWorkflowLayout>
    );
  }

  if (!inboundFile) {
    return <ErrorAlert message="No inbound file found for USRC referral instructions" />;
  }

  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <USRCReferralInstructionsContent referral={referral} sections={sections} />
        </div>
      </EhrInstructionsCard>

      <USRCReferralCards referral={referral} sections={sections} />
    </div>
  );
}
